import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { DateFormatterService } from '../../services/date-formatter.service';
import { personAgeStatusLabels, personAgeStatusOptions } from '@core/api/enums';
import { PersonAgeStatus } from '@core/api/generated/api-client';
import { PeopleApiService } from '@core/api/services/people-api.service';

export interface AgeStatusOption {
  value: PersonAgeStatus | null;
  label: string;
}

@Component({  
  selector: 'app-age-status-field',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './age-status-field.component.html',
  styleUrls: ['./age-status-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgeStatusFieldComponent implements OnInit, OnDestroy {
  @Input() birthDateControl!: FormControl;
  @Input() ageStatusOverrideControl!: FormControl;
  @Output() ageStatusOverrideChanged = new EventEmitter<PersonAgeStatus | null>();
  
  ageStatusOptions = personAgeStatusOptions;
  isCalculatingAgeStatus = false;

  private _calculatedAgeStatus: PersonAgeStatus | null = null;
  private _calculatedAge: number | null = null;
  private destroy$ = new Subject<void>();

  get hasManualOverride(): boolean {
    return this.ageStatusOverrideControl?.value != null;
  }

  get hasBirthDate(): boolean {
    return this.birthDateControl?.value != null;
  }

  get calculatedAgeStatusName(): string {
    if (this.hasManualOverride) {
      return personAgeStatusLabels[this.ageStatusOverrideControl.value as PersonAgeStatus] ?? this.ageStatusOverrideControl.value;
    }
    
    return this._calculatedAgeStatus ? personAgeStatusLabels[this._calculatedAgeStatus] ?? this._calculatedAgeStatus : '-';
  }

  get calculatedAge(): number | string {
    return this._calculatedAge ?? '-';
  }

  constructor(
    private peopleService: PeopleApiService,
    private dateFormatter: DateFormatterService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setupBirthDateListener();
    this.setupOverrideListener();
    this.initializeWithCurrentValues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeWithCurrentValues(): void {
    if (this.hasBirthDate) {
      if (!this.canCalculateFromBirthDate(this.birthDateControl.value)) {
        this.clearAgeStatusCalculation();
        return;
      }

      this.calculateAge(this.birthDateControl.value);
      if (!this.hasManualOverride) {
        this.fetchAgeStatus(this.birthDateControl.value);
      }
    }
  }

  private setupBirthDateListener(): void {
    this.birthDateControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500)
      )
      .subscribe((value: any) => {
        if (!value || !this.canCalculateFromBirthDate(value)) {
          this.clearAgeStatusCalculation();
          return;
        }

        this.calculateAge(value);
        if (!this.hasManualOverride) {
          this.fetchAgeStatus(value);
        }
      });
  }

  private setupOverrideListener(): void {
    this.ageStatusOverrideControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: PersonAgeStatus | null) => {
        if (value === null && this.hasBirthDate && this.canCalculateFromBirthDate(this.birthDateControl.value)) {
          this.fetchAgeStatus(this.birthDateControl.value);
        }
        this.ageStatusOverrideChanged.emit(value);
        this.cdr.markForCheck();
      });
  }

  private fetchAgeStatus(birthDate: any): void {
    if (!this.canCalculateFromBirthDate(birthDate)) {
      this.clearAgeStatusCalculation();
      return;
    }

    const birthDateString = this.dateFormatter.toISODateString(birthDate);

    if (!birthDateString) {
      this.clearAgeStatusCalculation();
      return;
    }

    this.isCalculatingAgeStatus = true;
    this.cdr.markForCheck();

    this.peopleService.calculateAgeStatus(birthDateString)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this._calculatedAgeStatus = response;
          this.isCalculatingAgeStatus = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error calculating age status:', error);
          this.clearAgeStatusCalculation();
        }
      });
  }

  private clearAgeStatusCalculation(): void {
    this._calculatedAgeStatus = null;
    this._calculatedAge = null;
    this.isCalculatingAgeStatus = false;
    this.cdr.markForCheck();
  }

  private calculateAge(birthDate: any): void {
    if (!birthDate) {
      this._calculatedAge = null;
      this.cdr.markForCheck();
      return;
    }

    try {
      const date = birthDate instanceof Date
        ? birthDate
        : this.dateFormatter.stringToLocalDate(String(birthDate));
      
      if (!date || isNaN(date.getTime())) {
        this._calculatedAge = null;
        return;
      }
      
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age--;
      }

      this._calculatedAge = age >= 0 ? age : null;
      this.cdr.markForCheck();
    } catch (error) {
      this._calculatedAge = null;
    }
  }

  private canCalculateFromBirthDate(birthDate: any): boolean {
    if (!birthDate || this.birthDateControl?.invalid) {
      return false;
    }

    const date = this.dateFormatter.toLocalDate(birthDate);

    if (!date || Number.isNaN(date.getTime())) {
      return false;
    }

    return date <= this.dateFormatter.getTodayAsDate();
  }

  onAgeStatusOverrideChange(event: any): void {
    this.ageStatusOverrideControl?.setValue(event.value);
  }

  openEditMode(): void {
    if (!this.hasManualOverride) {
      this.ageStatusOverrideControl?.setValue('Adult' satisfies PersonAgeStatus);
    }
    this.cdr.markForCheck();
  }

  onCloseEdit(): void {
    this.ageStatusOverrideControl?.setValue(null);
    this.cdr.markForCheck();
  }
}
